import {ApiResult, listPublishedDocuments, listReleasedLots, listShares, ShareKind} from "./api";

/**
 * The portal's headers, by the application each thing was shared FROM: the QMS (t1), Parkour-AI Documents (t11) and
 * SBOM (t4). Under QMS a sidebar lists the kinds of QMS object shared -- projects, folders, lots.
 *
 * NOTHING SHARED, NOTHING SHOWN, at both levels: a header with nothing under it is not in the menu, and neither is a
 * sidebar entry. A contact who was only ever sent a certificate has no reason to see an empty Projects page, and an
 * empty one reads as "something was withdrawn".
 *
 * A LIST THAT COULD NOT BE READ STAYS, so its page can say why. Hiding it would turn "the server failed" into "nothing
 * was shared with you", which sends the reader to the wrong person.
 */
export type Section = "qms" | "documents" | "sbom";
export type QmsItem = "projects" | "folders" | "lots";

export const SECTIONS: {key: Section; label: string}[] = [
    {key: "qms", label: "Foundation"},
    {key: "documents", label: "Parkour-AI Documents"},
    {key: "sbom", label: "SBOM"},
];

export const QMS_ITEMS: {key: QmsItem; label: string; probe: () => Promise<ApiResult>}[] = [
    {key: "projects", label: "Projects", probe: () => listShares("Project")},
    {key: "folders", label: "Folders", probe: () => listShares("Folder")},
    {key: "lots", label: "Lots", probe: listReleasedLots},
];

/** The share kind each customer-share page lists. */
export const SHARE_KIND: Partial<Record<Section | QmsItem, ShareKind>> = {documents: "Document", projects: "Project", folders: "Folder"};

/** How many things a list holds, or null when it could not be read. */
export function counted(result: ApiResult): number | null {
    if (result.status !== 200) return null;
    return (result.data?.Results ?? []).length;
}

/** True when an entry belongs in the menu: something is in it, or its list could not be read. */
export function shows(result: ApiResult): boolean {
    const count = counted(result);
    return count === null || count > 0;
}

export type Offered = {sections: Section[]; qms: {key: QmsItem; count: number | null}[]};

/** What to offer, in menu order: the headers, and under QMS its sidebar entries with their counts. */
export async function available(): Promise<Offered> {
    const [qmsResults, documents, sbom] = await Promise.all([
        Promise.all(QMS_ITEMS.map(one => one.probe())), listShares("Document"), listPublishedDocuments()]);
    const qms = QMS_ITEMS.map((one, index) => ({key: one.key, result: qmsResults[index]}))
        .filter(one => shows(one.result)).map(one => ({key: one.key, count: counted(one.result)}));
    const sections: Section[] = [];
    if (qms.length) sections.push("qms");
    if (shows(documents)) sections.push("documents");
    if (shows(sbom)) sections.push("sbom");
    return {sections, qms};
}
